import React, { useEffect, useState } from "react";
import axios from "../../api/Axios";
import { generateContractHTML } from "../../utils/contractGenerator";
import { 
  Table, 
  Badge, 
  Card,
  CardBody,
  Alert,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";

const StudentBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractHTML, setContractHTML] = useState('');
  const [currentBookingId, setCurrentBookingId] = useState(null);
  const [currentBookingStatus, setCurrentBookingStatus] = useState(null);
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api/accounts/voucher-bookings/");
      const bookingsData = response.data.results || response.data || [];
      setBookings(bookingsData);
      setError(null);
    } catch (err) {
      console.error("Error fetching bookings:", err);
      setError(`Failed to load bookings: ${err.response?.status || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openViewModal = (booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  const handleViewContract = async (booking) => {
    try {
      setContractLoading(true);
      
      // Fetch complete contract details from backend
      const response = await axios.get(`/api/accounts/voucher-bookings/${booking.id}/contract-details/`);
      const contractData = response.data;
      
      // Generate HTML contract
      const html = generateContractHTML(contractData);
      
      // Show in modal instead of new window
      setContractHTML(html);
      setCurrentBookingId(booking.id);
      setCurrentBookingStatus(booking.booking_status);
      setShowContractModal(true);
    } catch (err) {
      console.error("Error loading contract:", err);
      alert(`Failed to load contract: ${err.response?.data?.detail || err.message}`);
    } finally {
      setContractLoading(false);
    }
  };

  const handleAcceptContract = async () => {
    try {
      setContractLoading(true);
      
      const response = await axios.post(`/api/accounts/voucher-bookings/${currentBookingId}/accept-contract/`);
      
      if (response.status === 200 || response.status === 201) {
        alert('Thank you for accepting the lease agreement!\n\nThe provider will now review the contract.');
        setShowContractModal(false);
        // Refresh bookings list
        fetchBookings();
      }
    } catch (err) {
      console.error("Error accepting contract:", err);
      alert(`Failed to accept contract: ${err.response?.data?.error || err.message}`);
    } finally {
      setContractLoading(false);
    }
  };

  const handleDeclineContract = () => {
    if (window.confirm('Are you sure you want to decline this lease agreement?')) {
      setShowContractModal(false);
      alert('You have declined the lease agreement.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'warning', icon: 'clock-history', text: 'Pending BMO Approval' },
      'approved': { color: 'success', icon: 'check-circle', text: 'Approved by BMO' },
      'pending provider approval': { color: 'info', icon: 'clock-history', text: 'Pending Provider Approval' },
      'pending student contract acceptance': { color: 'primary', icon: 'file-earmark-text', text: 'Signature Required' },
      'pending provider contract acceptance': { color: 'info', icon: 'clock-history', text: 'Awaiting Provider' },
      'pending bmo contract acceptance': { color: 'info', icon: 'clock-history', text: 'Awaiting BMO' },
      'confirmed': { color: 'success', icon: 'check-circle', text: 'Confirmed' },
      'rejected': { color: 'danger', icon: 'x-circle', text: 'Rejected by BMO' },
      'cancelled': { color: 'danger', icon: 'x-circle', text: 'Cancelled' },
      'completed': { color: 'secondary', icon: 'check-circle-fill', text: 'Completed' }
    };

    const config = statusConfig[status] || { color: 'secondary', icon: 'question-circle', text: status };

    return (
      <Badge color={config.color} pill className="px-3 py-2">
        <i className={`bi bi-${config.icon} me-1`}></i>
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-ZA', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDurationDays = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return '-';
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  if (loading) {
    return (
      <Card>
        <CardBody>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading your bookings...</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert color="danger">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        {error}
        <Button 
          color="link" 
          size="sm" 
          onClick={fetchBookings}
          className="ms-2 text-danger"
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-0">My Bookings</h3>
          <span className="text-muted">Total: </span>
          <Badge color="primary" pill className="ms-2">{bookings.length}</Badge>
        </div>
        <Button 
          color="outline-primary" 
          size="sm" 
          onClick={fetchBookings}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </Button>
      </div>

      {bookings.length === 0 ? (
        <Alert color="info">
          <i className="bi bi-info-circle me-2"></i>
          You don't have any bookings yet. Start by browsing available properties!
        </Alert>
      ) : (
        <div className="table-responsive" style={{ paddingBottom: '200px' }}>
          <Table striped hover>
            <thead>
              <tr>
                <th>#</th>
                <th>Property</th>
                <th>Room/Bed</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Duration</th>
                <th>Status</th>
                <th>Booked</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((booking) => (
                <tr key={booking.id}>
                  <td><strong>{booking.id}</strong></td>
                  <td>
                    <div>
                      <strong>{booking.property_name || '-'}</strong>
                    </div>
                  </td>
                  <td>
                    <div>
                      {booking.booking_status !== 'pending' && booking.room_number ? (
                        <>
                          <div>
                            <strong>Room {booking.room_number}</strong>
                          </div>
                          {booking.bed_number && (
                            <small className="text-muted">Bed {booking.bed_number}</small>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(booking.check_in_date)}</td>
                  <td>{formatDate(booking.check_out_date)}</td>
                  <td>
                    <small className="text-muted">
                      {getDurationDays(booking.check_in_date, booking.check_out_date)}
                    </small>
                  </td>
                  <td>{getStatusBadge(booking.booking_status)}</td>
                  <td>
                    <small className="text-muted">{formatDateTime(booking.created_at)}</small>
                  </td>
                  <td className="text-center">
                    <UncontrolledDropdown>
                      <DropdownToggle color="outline-primary" size="sm">
                        ⋯
                      </DropdownToggle>
                      <DropdownMenu end>
                        <DropdownItem onClick={() => openViewModal(booking)}>
                          <i className="bi bi-eye me-2"></i>
                          View Details
                        </DropdownItem>
                        {booking.booking_status !== 'pending' && booking.booking_status !== 'pending provider approval' && (
                          <>
                            <DropdownItem divider />
                            <DropdownItem 
                              onClick={() => handleViewContract(booking)}
                              disabled={contractLoading}
                            >
                              <i className="bi bi-file-earmark-text me-2"></i>
                              {contractLoading ? 'Loading...' : 'View Contract'}
                            </DropdownItem>
                          </>
                        )}
                        {booking.booking_status === 'confirmed' && (
                          <>
                            <DropdownItem divider />
                            <DropdownItem>
                              <i className="bi bi-download me-2"></i>
                              Download Contract
                            </DropdownItem>
                          </>
                        )}
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Booking Details Modal */}
      <Modal isOpen={showModal} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          <i className="bi bi-calendar-check me-2"></i>
          Booking Details - #{selectedBooking?.id}
        </ModalHeader>
        <ModalBody>
          {selectedBooking && (
            <div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Status:</strong>
                  <div className="mt-2">
                    {getStatusBadge(selectedBooking.booking_status)}
                  </div>
                </div>
                <div className="col-md-6">
                  <strong>Booking ID:</strong>
                  <div className="mt-2">#{selectedBooking.id}</div>
                </div>
              </div>

              <hr />

              <div className="row mb-3">
                <div className="col-md-12">
                  <strong>Property:</strong>
                  <div className="mt-1">
                    <h5>{selectedBooking.property_name}</h5>
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Room:</strong>
                  <div className="mt-1">
                    {selectedBooking.booking_status !== 'pending' ? 
                      `Room ${selectedBooking.room_number || '-'}` : 'N/A'}
                  </div>
                </div>
                <div className="col-md-6">
                  <strong>Bed:</strong>
                  <div className="mt-1">
                    {selectedBooking.booking_status !== 'pending' ? 
                      `Bed ${selectedBooking.bed_number || '-'}` : 'N/A'}
                  </div>
                </div>
              </div>

              <hr />

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Check-in Date:</strong>
                  <div className="mt-1">{formatDate(selectedBooking.check_in_date)}</div>
                </div>
                <div className="col-md-6">
                  <strong>Check-out Date:</strong>
                  <div className="mt-1">{formatDate(selectedBooking.check_out_date)}</div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <strong>Duration:</strong>
                  <div className="mt-1">
                    {getDurationDays(selectedBooking.check_in_date, selectedBooking.check_out_date)}
                  </div>
                </div>
                <div className="col-md-6">
                  <strong>Booked On:</strong>
                  <div className="mt-1">{formatDateTime(selectedBooking.created_at)}</div>
                </div>
              </div>

              {selectedBooking.total_price && (
                <>
                  <hr />
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <strong>Total Price:</strong>
                      <div className="mt-1">
                        <h5 className="text-primary mb-0">
                          R {parseFloat(selectedBooking.total_price).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </h5>
                      </div>
                    </div>
                    {selectedBooking.price_per_night && (
                      <div className="col-md-6">
                        <strong>Price per Night:</strong>
                        <div className="mt-1 text-muted">
                          R {parseFloat(selectedBooking.price_per_night).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {selectedBooking.special_requests && (
                <>
                  <hr />
                  <div className="mb-3">
                    <strong>Special Requests:</strong>
                    <div className="mt-2 p-3 bg-light rounded">
                      {selectedBooking.special_requests}
                    </div>
                  </div>
                </>
              )}

              {selectedBooking.notes && (
                <div className="mb-3">
                  <strong>Notes:</strong>
                  <div className="mt-2 p-3 bg-light rounded">
                    {selectedBooking.notes}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedBooking?.status === 'pending student contract acceptance' && (
            <Button color="success">
              <i className="bi bi-file-earmark-check me-2"></i>
              Accept Contract
            </Button>
          )}
          {selectedBooking?.status === 'confirmed' && (
            <Button color="primary">
              <i className="bi bi-download me-2"></i>
              Download Contract
            </Button>
          )}
          <Button color="secondary" onClick={closeModal}>
            Close
          </Button>
        </ModalFooter>
      </Modal>

      {/* Contract Viewing Modal */}
      <Modal isOpen={showContractModal} toggle={() => setShowContractModal(false)} size="xl" style={{maxWidth: '95%'}}>
        <ModalHeader toggle={() => setShowContractModal(false)}>
          Lease Agreement
        </ModalHeader>
        <ModalBody style={{maxHeight: 'calc(100vh - 300px)', overflowY: 'auto', padding: 0}}>
          <div dangerouslySetInnerHTML={{ __html: contractHTML }} />
        </ModalBody>
        <ModalFooter style={{backgroundColor: '#f8f9fa', borderTop: '3px solid #36669f'}}>
          <div style={{width: '100%'}}>
            {currentBookingStatus === 'pending student contract acceptance' ? (
              <>
                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                    <input 
                      type="checkbox" 
                      checked={agreeToTerms}
                      onChange={(e) => setAgreeToTerms(e.target.checked)}
                      style={{width: '20px', height: '20px'}}
                    />
                    <strong>I have read and agree to the terms and conditions of this Student Lease Agreement.</strong>
                  </label>
                </div>
                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <Button color="secondary" onClick={handleDeclineContract} disabled={contractLoading}>
                    Decline
                  </Button>
                  <Button 
                    color="primary" 
                    onClick={handleAcceptContract} 
                    disabled={!agreeToTerms || contractLoading}
                  >
                    {contractLoading ? 'Processing...' : 'Accept Agreement'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div style={{marginBottom: '15px'}}>
                  <label style={{display: 'flex', alignItems: 'center', gap: '10px', opacity: 0.6, cursor: 'not-allowed'}}>
                    <input 
                      type="checkbox" 
                      checked={true}
                      disabled={true}
                      style={{width: '20px', height: '20px'}}
                    />
                    <strong>I have read and agree to the terms and conditions of this Student Lease Agreement.</strong>
                  </label>
                  <div style={{marginTop: '10px', padding: '10px', backgroundColor: '#d1ecf1', borderLeft: '4px solid #0c5460', borderRadius: '4px'}}>
                    <i className="bi bi-check-circle-fill me-2" style={{color: '#0c5460'}}></i>
                    <strong style={{color: '#0c5460'}}>Contract Accepted</strong>
                    <p style={{margin: '5px 0 0 0', color: '#0c5460', fontSize: '14px'}}>
                      You have already accepted this contract. Awaiting provider acceptance.
                    </p>
                  </div>
                </div>
                <div style={{display: 'flex', gap: '10px', justifyContent: 'flex-end'}}>
                  <Button color="secondary" onClick={() => setShowContractModal(false)}>
                    Close
                  </Button>
                </div>
              </>
            )}
          </div>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default StudentBookings;
